import { useCallback, useEffect, useRef } from 'react';
import { inIframe, isInput } from './menuSearchBarcodeReader.utils';
export const MenuSearchBarcodeReader = ({ onScanButtonLongPressed, scanButtonLongPressThreshold = 3, onScan, onError, onKeyDetect, onReceive, minLength = 6, avgTimeByChar = 30, scanButtonKeyCode, stopPropagation = false, preventDefault = false, endChar = [9, 13], startChar = [], timeBeforeScanTest = 100, }) => {
    const firstCharTime = useRef(0);
    const lastCharTime = useRef(0);
    const stringWriting = useRef('');
    const callIsScanner = useRef(false);
    const testTimer = useRef(null);
    const scanButtonCounter = useRef(0);
    const initScannerDetection = () => {
        firstCharTime.current = 0;
        stringWriting.current = '';
        scanButtonCounter.current = 0;
    };
    // Scanner detection logic
    const scannerDetectionTest = useCallback((s) => {
        // If string is given, test it
        if (s) {
            firstCharTime.current = 0;
            lastCharTime.current = 0;
            stringWriting.current = s;
        }
        if (!scanButtonCounter.current) {
            scanButtonCounter.current = 1;
        }
        if (stringWriting.current.length >= minLength &&
            lastCharTime.current - firstCharTime.current < stringWriting.current.length * avgTimeByChar) {
            if (onScanButtonLongPressed && scanButtonCounter.current > scanButtonLongPressThreshold)
                onScanButtonLongPressed(stringWriting.current, scanButtonCounter.current);
            else if (onScan)
                onScan(stringWriting.current, scanButtonCounter.current);
            initScannerDetection();
            return true;
        }
        let errorMsg = '';
        if (stringWriting.current.length < minLength) {
            errorMsg = `String length should be greater or equal ${minLength}`;
        }
        else {
            if (lastCharTime.current - firstCharTime.current > stringWriting.current.length * avgTimeByChar) {
                errorMsg = `Average key character time should be less or equal ${avgTimeByChar}ms`;
            }
        }
        if (onError)
            onError(stringWriting.current, errorMsg);
        initScannerDetection();
        return false;
    }, [minLength, avgTimeByChar, onScanButtonLongPressed, scanButtonLongPressThreshold, onScan, onError]);
    // Key press handler
    const handleKeyPress = useCallback((e) => {
        const { target } = e;
        if (target instanceof window.HTMLElement && isInput(target)) {
            return;
        }
        if (scanButtonKeyCode && e.which === scanButtonKeyCode) {
            scanButtonCounter.current += 1;
            e.preventDefault();
            e.stopImmediatePropagation();
        }
        if (onKeyDetect)
            onKeyDetect(e);
        if (stopPropagation)
            e.stopImmediatePropagation();
        if (preventDefault)
            e.preventDefault();
        if (firstCharTime.current && endChar.indexOf(e.which) !== -1) {
            e.preventDefault();
            e.stopImmediatePropagation();
            callIsScanner.current = true;
        }
        else if (!firstCharTime.current && startChar.indexOf(e.which) !== -1) {
            e.preventDefault();
            e.stopImmediatePropagation();
            callIsScanner.current = false;
        }
        else {
            if (typeof e.which !== 'undefined') {
                stringWriting.current += String.fromCharCode(e.which);
            }
            callIsScanner.current = false;
        }
        if (!firstCharTime.current) {
            firstCharTime.current = Date.now();
        }
        lastCharTime.current = Date.now();
        if (testTimer.current)
            clearTimeout(testTimer.current);
        if (callIsScanner.current) {
            scannerDetectionTest();
            testTimer.current = null;
        }
        else {
            testTimer.current = setTimeout(() => scannerDetectionTest(), timeBeforeScanTest);
        }
        if (onReceive)
            onReceive(e);
    }, [
        scanButtonKeyCode,
        onKeyDetect,
        stopPropagation,
        preventDefault,
        endChar,
        startChar,
        timeBeforeScanTest,
        onReceive,
        scannerDetectionTest,
    ]);
    // Attach/detach event listeners
    useEffect(() => {
        const handler = handleKeyPress;
        if (inIframe()) {
            window.parent.document.addEventListener('keypress', handler);
        }
        window.document.addEventListener('keypress', handler);
        return () => {
            if (inIframe()) {
                window.parent.document.removeEventListener('keypress', handler);
            }
            window.document.removeEventListener('keypress', handler);
        };
    }, [handleKeyPress]);
    return null;
};
